import * as React from "react";
import { cn } from "./cn";

type FieldRowProps = {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
};

type ControlProps = {
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: React.AriaAttributes["aria-invalid"];
};

/**
 * One field: label, ONE control, and a hint or an error.
 *
 * The contract is "one control element" — `Input`, `PasswordInput`, `Select`,
 * `Textarea`; every caller already passes exactly that. It used to be a single
 * `<label>` wrapped around the control, the hint and the error, which put
 * PasswordInput's show/hide button inside the label too (two controls, one
 * label) and left the hint and the error unconnected to the input: a screen
 * reader focusing the field heard its name and nothing about "at least 8
 * characters" or "wrong password". Now the label points at the control
 * (`htmlFor`), the note is `aria-describedby`, and an error sets `aria-invalid`.
 * A control's own `id` / `aria-describedby` are kept.
 */
export function FieldRow({ label, hint, error, children, className }: FieldRowProps) {
  const uid = React.useId();
  const control = React.isValidElement<ControlProps>(children) ? children : null;
  const id = control?.props.id ?? `${uid}-control`;
  const note = error ?? hint;
  const noteId = note ? `${uid}-note` : undefined;
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {label ? (
        <label
          htmlFor={control ? id : undefined}
          className="font-display font-semibold text-[14px] leading-5 text-foreground"
        >
          {label}
        </label>
      ) : null}
      {control
        ? React.cloneElement(control, {
            id,
            "aria-describedby":
              [control.props["aria-describedby"], noteId].filter(Boolean).join(" ") || undefined,
            "aria-invalid": error ? true : control.props["aria-invalid"],
          })
        : children}
      {error ? (
        <span id={noteId} role="alert" className="text-[12px] leading-4 text-destructive">
          {error}
        </span>
      ) : hint ? (
        <span id={noteId} className="text-[12px] leading-4 text-mid-gray">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

// N24 field anatomy: white surface, 1px tinted border, rounded, ring focus
// (was: square borderless off-white with a 2px blue focus border). The ink is
// the card surface's own pair — it was a fixed near-black on a themeable
// background, so a dark card theme made every form in the app unreadable.
const inputBase =
  "w-full bg-card text-card-foreground placeholder:text-medium-gray px-4 py-3 h-[50px] leading-6 rounded-lg border border-border focus:border-primary focus:ring-2 focus:ring-ring/40 focus:outline-none disabled:bg-light-gray disabled:text-medium-gray";

export const Input = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(function Input({ className, ...props }, ref) {
  return <input ref={ref} className={cn(inputBase, className)} {...props} />;
});

export const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      className={cn(inputBase, "min-h-[120px] h-auto py-3", className)}
      {...props}
    />
  );
});

export const Select = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>(function Select({ className, children, ...props }, ref) {
  return (
    <select ref={ref} className={cn(inputBase, className)} {...props}>
      {children}
    </select>
  );
});

type CheckboxProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label: React.ReactNode;
};

export function Checkbox({ label, className, ...props }: CheckboxProps) {
  return (
    <label className={cn("inline-flex items-center gap-3 cursor-pointer select-none", className)}>
      <input
        type="checkbox"
        className="appearance-none w-6 h-6 rounded-md border border-border bg-white checked:bg-primary checked:border-primary grid place-items-center focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 relative after:content-[''] after:hidden checked:after:block after:absolute after:w-2 after:h-3 after:border-b-2 after:border-r-2 after:border-white after:rotate-45 after:-mt-1"
        {...props}
      />
      <span className="text-sm leading-5 text-body">{label}</span>
    </label>
  );
}
